/**
 * Mother Registration Types
 *
 * Zod schemas and types for the multi-step mother registration form.
 */
import { z } from 'zod';

const isAtLeast18YearsOld = (dateStr: string) => {
  const today = new Date();
  const birthDate = new Date(dateStr);
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    return age - 1 >= 18;
  }
  return age >= 18;
};

// -- Step 1: Demographics ---------------------------------------
export const ChildSchema = z.object({
  name: z.string().nullish(),
  dob: z.string().nullish(),
  status: z.enum(['fullTerm', 'premature', 'deceased', 'unknown', '']).nullish(),
  linkId: z.string().nullish(),
  /** Display label for pre-selected patient (used as fallback when patientOptions hasn't loaded). */
  patientLabel: z.string().nullish(),
});

export const EmergencyContactSchema = z.object({
  // Validation messages are i18n KEYS (not literal English) — translated at render
  // time by the consuming dialog, since Zod schemas have no access to t().
  name: z.string().regex(/^[^0-9]*$/, 'mother.validation.contactNameNoNumbers').nullish(),
  phone: z.string().regex(/^\+?[\d\s]*$/, 'mother.validation.phoneFormat').nullish().or(z.literal('')),
  relationship: z.string().nullish(),
});

export const ExclusionDateRangeSchema = z.object({
  start: z.string().min(1, 'mother.validation.exclusionStartRequired'),
  end: z.string().nullish(),
});

export type ExclusionDateRange = z.infer<typeof ExclusionDateRangeSchema>;

export const DemographicsSchema = z.object({
  // Validation messages are i18n KEYS (not literal English) — translated at render
  // time by the consuming dialog, since Zod schemas have no access to t().
  role: z.array(z.enum(['mother', 'donor'])).min(1, 'mother.validation.selectRole'),
  mrn: z.string().min(1, 'mother.validation.mrnRequired').refine((v) => !/\s/.test(v), 'mother.validation.mrnNoSpaces'),
  firstName: z.string().min(1, 'mother.validation.firstNameRequired').regex(/^[^0-9]*$/, 'mother.validation.firstNameNoNumbers'),
  lastName: z.string().min(1, 'mother.validation.lastNameRequired').regex(/^[^0-9]*$/, 'mother.validation.lastNameNoNumbers'),
  dateOfBirth: z.string().nullish(),
  phone: z.string().regex(/^\+?[\d\s]*$/, 'mother.validation.phoneFormat').nullish().or(z.literal('')),
  email: z.string().email('mother.validation.emailInvalid').nullish().or(z.literal('')),
  streetAddress: z.string().nullish(),
  city: z.string().nullish(),
  zipCode: z.string().max(20, 'mother.validation.zipCodeMaxLength').nullish(),
  hospitalLocation: z.string().nullish(),
  unit: z.string().nullish(),
  bed: z.string().nullish(),
  exclusionDates: z.array(ExclusionDateRangeSchema).nullish(),
  milkStartDate: z.string().nullish(),
  donationConclusionDate: z.string().nullish(),
  children: z.array(ChildSchema).nullish(),
  emergencyContact: EmergencyContactSchema.nullish(),
}).superRefine((data, ctx) => {
  const isMother = data.role?.includes('mother');
  const isDonor = data.role?.includes('donor');

  // Reject duplicate patient selections (applies to Mother / Both roles)
  if (isMother) {
    const seen = new Set<string>();
    (data.children ?? []).forEach((c, i) => {
      const id = c.name;
      if (typeof id !== 'string' || id.length === 0) return;
      if (seen.has(id)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'mother.registration.duplicateChild', path: ['children', i, 'name'] });
      } else {
        seen.add(id);
      }
    });
  }

  if (data.milkStartDate && data.donationConclusionDate && data.donationConclusionDate < data.milkStartDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'mother.validation.donationConclusionDateBeforeMilkStart',
      path: ['donationConclusionDate'],
    });
  }

  if (!isDonor) return;

  const isDonorOnly = isDonorOnlyRole(data.role ?? []);

  // All fields required for donor except exclusionDates. Each message is a
  // pre-composed i18n key (not interpolated at schema-build time) so the
  // translation step at render time is a plain t(key) call.
  const requiredFields: { key: string; messageKey: string; value: unknown }[] = [
    { key: 'phone', messageKey: 'mother.validation.donorPhoneRequired', value: data.phone },
    { key: 'email', messageKey: 'mother.validation.donorEmailRequired', value: data.email },
    { key: 'streetAddress', messageKey: 'mother.validation.donorStreetAddressRequired', value: data.streetAddress },
    { key: 'city', messageKey: 'mother.validation.donorCityRequired', value: data.city },
    { key: 'zipCode', messageKey: 'mother.validation.donorZipCodeRequired', value: data.zipCode },
    { key: 'milkStartDate', messageKey: 'mother.validation.donorMilkStartDateRequired', value: data.milkStartDate },
  ];

  for (const { key, messageKey, value } of requiredFields) {
    if (!value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: messageKey,
        path: [key],
      });
    }
  }

  // Validate donor age (must be at least 18 years old)
  if (data.dateOfBirth && !isAtLeast18YearsOld(data.dateOfBirth)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'mother.validation.donorAgeRequired',
      path: ['dateOfBirth'],
    });
  }

  // Emergency contact is optional for all roles, including donors (per policy: staff
  // may add it if known, but it must never block registration/approval/portal access).

  // Donor-only child validation: DOB and status are now optional.
  // For BOTH (mother+donor) the UI collects children via patient selection
  // (name + DOB, no status) and the request builders skip child_details.
  if (isDonorOnly) {
    const allChildren = data.children ?? [];

    // If a child DOB is provided, validate that it's in a valid calendar format.
    // Iterate over the original array so `i` matches the real row index.
    allChildren.forEach((c, i) => {
      if (!c.dob) return; // skip empty DOB
      // Strict calendar validation: parse components and round-trip to catch
      // dates like 2024-02-31 that JS Date silently normalises.
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(c.dob);
      if (!match) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'mother.registration.childDobInvalid', path: ['children', i, 'dob'] });
      } else {
        const [, y, m, d] = match;
        const date = new Date(`${y}-${m}-${d}T00:00:00Z`);
        if (
          isNaN(date.getTime()) ||
          date.getUTCFullYear() !== Number(y) ||
          date.getUTCMonth() + 1 !== Number(m) ||
          date.getUTCDate() !== Number(d)
        ) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'mother.registration.childDobInvalid', path: ['children', i, 'dob'] });
        }
      }
    });
  }
});

export type DemographicsFormValues = z.infer<typeof DemographicsSchema>;
export type Child = z.infer<typeof ChildSchema>;
export type EmergencyContact = z.infer<typeof EmergencyContactSchema>;

/** Check whether a roles array represents donor-only (no mother). */
export function isDonorOnlyRole(roles: string[]): boolean {
  return roles.includes('donor') && !roles.includes('mother');
}

// -- Step 2: Consent & Documents --------------------------------
export const ConsentDocumentSchema = z.object({
  fileName: z.string(),
  fileSize: z.number(),
  uploadedAt: z.string(),
  file: z.any(), // File object
});

export const ConsentSchema = z.object({
  agreedToTerms: z.boolean().optional(),
  signature: z.string().optional(),
  documents: z.array(ConsentDocumentSchema).optional(),
});

export type ConsentFormValues = z.infer<typeof ConsentSchema>;
export type ConsentDocument = z.infer<typeof ConsentDocumentSchema>;

// -- Step 3: Health Screening -----------------------------------
export const HealthScreeningSchema = z.object({
  // Medical History
  medicalConditions: z.array(z.string()).optional(),
  pastMedicalHistory: z.string().optional(),
  currentMedications: z.string().optional(),
  allergies: z.string().optional(),
  historyOfMastitis: z.boolean().optional(),

  // Lifestyle Factors
  nicotineUse: z.enum(['yes', 'no', '']).optional(),
  alcoholUse: z.string().optional(),
  dietType: z.string().optional(),
  warning: z.string().optional(),
});

export type HealthScreeningFormValues = z.infer<typeof HealthScreeningSchema>;

/**
 * Validates health screening fields that are mandatory for donors.
 * Returns a record of field -> error message for any missing required fields.
 */
export function validateDonorHealthScreening(data: HealthScreeningFormValues): Record<string, string> {
  // Returns i18n KEYS (not literal English) — translated at render time by the
  // consuming dialog, since this plain function has no access to t().
  const errors: Record<string, string> = {};
  if (!data.pastMedicalHistory) errors['pastMedicalHistory'] = 'mother.validation.pastMedicalHistoryRequired';
  if (!data.currentMedications) errors['currentMedications'] = 'mother.validation.currentMedicationsRequired';
  if (!data.nicotineUse) errors['nicotineUse'] = 'mother.validation.nicotineUseRequired';
  if (!data.alcoholUse) errors['alcoholUse'] = 'mother.validation.alcoholUseRequired';
  if (!data.dietType) errors['dietType'] = 'mother.validation.dietTypeRequired';
  return errors;
}

// -- Combined Registration Payload ------------------------------
export const MotherRegistrationSchema = z.object({
  demographics: DemographicsSchema,
  consent: ConsentSchema,
  healthScreening: HealthScreeningSchema,
});

export type MotherRegistrationPayload = z.infer<typeof MotherRegistrationSchema>;

// -- Edit mode pre-populated data -------------------------------
export type MotherFormData = {
  id: string;
  demographics: DemographicsFormValues;
  consent: ConsentFormValues;
  healthScreening: HealthScreeningFormValues;
  /** Whether the donor role is currently active; false when the donor role has been discharged. */
  isActiveDonor?: boolean;
};

// -- Stepper Definitions ----------------------------------------
export type RegistrationStep = 'demographics' | 'consent' | 'healthScreening' | 'review';

export interface StepConfig {
  key: RegistrationStep;
  label: string;
  index: number;
}

export const REGISTRATION_STEPS: StepConfig[] = [
  { key: 'demographics', label: 'Demographics', index: 0 },
  { key: 'consent', label: 'Consent & Documents', index: 1 },
  { key: 'healthScreening', label: 'Health Screening', index: 2 },
  { key: 'review', label: 'Review & Submit', index: 3 },
];
